import React, { useState } from 'react';
import moment from 'moment';
import { t } from 'i18next';
import EmptyState from '../util/EmptyState';
import CompanyDetail from './CompanyDetail';

function stringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${hash % 360},50%,45%)`;
}

function getPaidStatus(date?: string | null) {
  if (!date) return null;
  const days = moment(date).diff(moment(), 'days');
  if (days < 0) return 'expired';
  if (days <= 14) return 'warning';
  return 'active';
}

interface Props {
  companyRow: any[];
  tariffRow: any[];
  baseUrl: string;
  onRefresh: () => void;
}

const CompanyCards: React.FC<Props> = ({ companyRow, tariffRow, baseUrl, onRefresh }) => {
  const [detailId, setDetailId] = useState<number | null>(null);

  if (!Array.isArray(companyRow) || companyRow.length === 0) {
    return <EmptyState icon="🏢" message={t('No company available') as string} />;
  }

  const getTariff = (id: number | null) => tariffRow.find((t: any) => t.id === id) ?? null;
  const detailCompany = detailId ? companyRow.find((c: any) => c.id === detailId) ?? null : null;

  const totalUsers = companyRow.reduce((s, c) => s + (c.users_number ?? 0), 0);
  const expiringSoon = companyRow.filter(c => getPaidStatus(c.paid_until) === 'warning').length;
  const expired = companyRow.filter(c => getPaidStatus(c.paid_until) === 'expired').length;

  const stats = [
    { icon: '🏢', label: t('Companies'), value: companyRow.length, bg: 'var(--color-info-bg,#e3f2fd)' },
    { icon: '👥', label: t('Users'),     value: totalUsers,          bg: 'var(--color-success-bg,#e8f5e9)' },
    { icon: '⚠️', label: t('Expiring'),  value: expiringSoon,        bg: 'var(--color-warning-bg,#fff3e0)' },
    { icon: '⛔', label: t('Expired'),   value: expired,             bg: 'var(--color-danger-bg,#ffebee)' },
  ];

  return (
    <div className="flex gap-0 overflow-hidden">
      <div className="flex-1 min-w-0">
        {/* Stats bar */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 }}>
          {stats.map(s => (
            <div
              key={s.label}
              className="bg-[var(--btn-user-form)] rounded-[8px] p-[14px] flex items-center gap-[12px] border border-[var(--border-input)]">
              <div
                className="w-[38px] h-[38px] rounded-[8px] flex items-center justify-center text-[18px] flex-shrink-0"
                style={{ background: s.bg }}>
                {s.icon}
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-color)] leading-none">{s.value}</div>
                <div className="text-[11px] font-semibold text-[var(--text-color-calendar-week)] mt-[2px]">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Cards grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(270px,1fr))', gap: 12 }}>
          {companyRow.map((company: any) => {
            const tariff = getTariff(company.tariffTypeId);
            const status = getPaidStatus(company.paid_until);
            const initials = (company.title ?? '?').slice(0, 2).toUpperCase();
            const color = stringToColor(company.title ?? '');
            const activeModules = tariff?.allowed_modules?.length ?? 0;
            const isSelected = detailId === company.id;

            return (
              <div
                key={company.id}
                onClick={() => setDetailId(isSelected ? null : company.id)}
                className={`bg-[var(--btn-user-form)] rounded-[10px] border cursor-pointer overflow-hidden transition-all
                  ${isSelected
                    ? 'border-[#2563eb] shadow-[0_4px_16px_rgba(37,99,235,.15)]'
                    : 'border-[var(--border-input)] hover:border-[#93c5fd] hover:shadow-[0_4px_16px_rgba(37,99,235,.08)]'
                  }`}>

                {/* Header */}
                <div className="flex items-center gap-[12px] p-[14px] pb-[10px]">
                  {company.logoUrl ? (
                    <img
                      src={`${baseUrl}${company.logoUrl}`}
                      alt={company.title}
                      className="w-[40px] h-[40px] rounded-[8px] object-contain flex-shrink-0"
                    />
                  ) : (
                    <div
                      className="w-[40px] h-[40px] rounded-[8px] flex items-center justify-center text-white text-[13px] font-bold flex-shrink-0"
                      style={{ background: color }}>
                      {initials}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[14px] text-[var(--text-color)] truncate">{company.title}</div>
                    <div className="text-[11px] text-[var(--text-color-calendar-week)] mt-[1px]">
                      {tariff ? tariff.name : '—'}
                    </div>
                  </div>
                  {status && (
                    <span className={`px-[7px] py-[2px] rounded-[10px] text-[10px] font-bold flex-shrink-0 ${
                      status === 'active'  ? 'bg-[var(--color-success-bg,#e8f5e9)] text-[var(--color-success,#2e7d32)]' :
                      status === 'warning' ? 'bg-[var(--color-warning-bg,#fff3e0)] text-[var(--color-warning,#e65100)]' :
                                            'bg-[var(--color-danger-bg,#ffebee)] text-[var(--color-danger,#c62828)]'
                    }`}>
                      {status === 'active' ? 'Active' : status === 'warning' ? 'Expiring' : 'Expired'}
                    </span>
                  )}
                </div>

                {/* Mini stats */}
                <div className="grid grid-cols-2 gap-[6px] px-[14px] pb-[10px]">
                  <div className="bg-[var(--bg-form)] rounded-[6px] p-[8px]">
                    <div className="text-[15px] font-bold text-[var(--text-color)]">{company.users_number ?? 0}</div>
                    <div className="text-[10px] font-semibold text-[var(--text-color-calendar-week)]">{t('Users')}</div>
                  </div>
                  <div className="bg-[var(--bg-form)] rounded-[6px] p-[8px]">
                    <div className="text-[15px] font-bold text-[var(--text-color)]">{activeModules || '—'}</div>
                    <div className="text-[10px] font-semibold text-[var(--text-color-calendar-week)]">Modules</div>
                  </div>
                </div>

                {/* Footer */}
                <div className="border-t border-[var(--border-input)] px-[14px] py-[8px] flex items-center justify-between">
                  <div className="text-[11px] text-[var(--text-color-calendar-week)]">
                    {company.paid_until ? (
                      <>
                        Paid until{' '}
                        <span className={`font-semibold ${
                          status === 'expired' ? 'text-[var(--color-danger,#c62828)]' :
                          status === 'warning' ? 'text-[var(--color-warning,#e65100)]' :
                                                 'text-[var(--color-success,#2e7d32)]'
                        }`}>
                          {moment(company.paid_until).format('DD.MM.YYYY')}
                        </span>
                      </>
                    ) : (
                      'No payment info'
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--text-color-calendar-week)]">
                    {company.last_login ? moment(company.last_login).fromNow() : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail panel */}
      {detailId && detailCompany && (
        <CompanyDetail
          company={detailCompany}
          tariffRow={tariffRow}
          onClose={() => setDetailId(null)}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};

export default CompanyCards;
